import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { db, auth } from "./firebase";
import { collection, addDoc, onSnapshot, doc, updateDoc, deleteDoc, query, where, arrayUnion, arrayRemove } from "firebase/firestore";
import { getPodColor } from "./podColors";
import { timeAgo } from "./dateUtils";
import { BackIcon, PlusIcon, TrashIcon } from "./Pods";

const MENU_ICON = <svg className="pg-icon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="5" cy="12" r="1.3" /><circle cx="12" cy="12" r="1.3" /><circle cx="19" cy="12" r="1.3" /></svg>;
const CHECK_ICON = <svg className="pg-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>;
const SEARCH_ICON = <svg className="pg-icon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6.5" /><path d="M20 20l-4-4" /></svg>;

// List groups by last change: Today / This week (last 7 days) / Earlier
function groupOf(ts) {
  if (!ts) return "Earlier";
  const d = new Date(ts), now = new Date();
  if (d.toDateString() === now.toDateString()) return "Today";
  return now - d < 7 * 86400000 ? "This week" : "Earlier";
}

// intent (from the Dashboard): { noteId } selects that note once loaded; { noteId: null } opens "Add Note".
export default function Notes({ intent = null, onIntentDone = () => {} }) {
  const [allNotes, setAllNotes] = useState([]);
  const [notes, setNotes] = useState([]);
  const [pods, setPods] = useState({});
  const [contacts, setContacts] = useState([]);
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // "all" | "mine" | podId
  const [menu, setMenu] = useState(null); // "share" | "more" while a menu is open
  const [saveState, setSaveState] = useState("saved"); // "saving" while an edit is pending
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  const deletingRef = useRef(false);
  const editorRef = useRef(null);
  const allNotesRef = useRef([]);
  const autosaveRef = useRef(null); // { noteId, title, desc, timer } while an autosave is pending
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 640);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Create form
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedPodIds, setSelectedPodIds] = useState([]);

  // Inline edit state — synced to selectedNote
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editSharedPods, setEditSharedPods] = useState([]);

  const currentUser = auth.currentUser;
  const currentUserEmail = currentUser?.email ?? "";
  const currentUserUid = currentUser?.uid ?? "";

  // Fetch notes: owned by this user (catches legacy notes) + shared via memberEmails.
  useEffect(() => {
    if (!currentUserUid || !currentUserEmail) return;

    const ownedNotes = {};
    const sharedNotes = {};
    const mergeAndSet = () => {
      const combined = { ...sharedNotes, ...ownedNotes };
      setAllNotes(Object.values(combined));
    };

    const q1 = query(collection(db, "notes"), where("ownerUid", "==", currentUserUid));
    const unsub1 = onSnapshot(q1, (snap) => {
      Object.keys(ownedNotes).forEach((k) => delete ownedNotes[k]);
      snap.docs.forEach((d) => { ownedNotes[d.id] = { id: d.id, ...d.data() }; });
      mergeAndSet();
    });

    const q2 = query(collection(db, "notes"), where("memberEmails", "array-contains", currentUserEmail));
    const unsub2 = onSnapshot(q2, (snap) => {
      Object.keys(sharedNotes).forEach((k) => delete sharedNotes[k]);
      snap.docs.forEach((d) => { sharedNotes[d.id] = { id: d.id, ...d.data() }; });
      mergeAndSet();
    });

    return () => { unsub1(); unsub2(); };
  }, [currentUserUid, currentUserEmail]);

  useEffect(() => { allNotesRef.current = allNotes; }, [allNotes]);

  // Leaving the Notes page with an autosave still pending: save it now instead of dropping it
  useEffect(() => () => {
    const p = autosaveRef.current;
    if (p) { clearTimeout(p.timer); saveNoteContent(p.noteId, p.title, p.desc); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update visible notes whenever allNotes (owned + memberEmails) or pods changes.
  useEffect(() => {
    const visible = allNotes.filter((n) => {
      if ((n.hiddenBy || []).includes(currentUserEmail)) return false;
      return (
        n.ownerUid === currentUserUid ||
        (!n.ownerUid && n.ownerEmail === currentUserEmail) ||
        (n.sharedPods || []).some((id) => id in pods)
      );
    });
    // Most recently edited first. Snapshots are live, so anyone's saved edit moves that note to the top.
    visible.sort((a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0));
    setNotes(visible);
  }, [allNotes, pods, currentUserUid, currentUserEmail]);

  // Keep selectedNoteId pointing at a visible note, but don't auto-select after deletion
  useEffect(() => {
    if (deletingRef.current) { deletingRef.current = false; return; }
    if (notes.length === 0) { setSelectedNoteId(null); return; }
    setSelectedNoteId((prev) => {
      if (prev && notes.find((n) => n.id === prev)) return prev;
      return notes[0].id;
    });
  }, [notes]);

  // Dashboard hand-off: open the requested note (once it has loaded) or the Add Note form
  useEffect(() => {
    if (!intent) return;
    if (intent.noteId === null) { setShowAddNoteModal(true); onIntentDone(); return; }
    if (!notes.some((n) => n.id === intent.noteId)) return;
    setSelectedNoteId(intent.noteId);
    if (isMobile) setMobileDetailOpen(true);
    onIntentDone();
  }, [intent, notes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync inline edit fields whenever the selected note changes
  useEffect(() => {
    if (selectedNote) {
      setEditTitle(selectedNote.title || "");
      setEditDescription(selectedNote.description || "");
      setEditSharedPods(selectedNote.sharedPods || []);
      setMenu(null);
    }
  }, [selectedNoteId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currentUserUid || !currentUserEmail) return;

    // Two queries for pods: ones the user owns, and ones they're a member of.
    const ownedPods = {};
    const memberPods = {};
    const mergePods = () => {
      const merged = { ...memberPods, ...ownedPods };
      // Only include pods with a name (guards against malformed docs).
      setPods(Object.fromEntries(Object.entries(merged).filter(([, p]) => p.name)));
    };

    const qPods1 = query(collection(db, "pods"), where("ownerUid", "==", currentUserUid));
    const unsubPods1 = onSnapshot(qPods1, (snap) => {
      Object.keys(ownedPods).forEach((k) => delete ownedPods[k]);
      snap.docs.forEach((d) => { ownedPods[d.id] = { id: d.id, ...d.data() }; });
      mergePods();
    });

    const qPods2 = query(collection(db, "pods"), where("members", "array-contains", currentUserEmail));
    const unsubPods2 = onSnapshot(qPods2, (snap) => {
      Object.keys(memberPods).forEach((k) => delete memberPods[k]);
      snap.docs.forEach((d) => { memberPods[d.id] = { id: d.id, ...d.data() }; });
      mergePods();
    });

    // Contacts are strictly private — filter by ownerUid server-side.
    const qContacts = query(collection(db, "contacts"), where("ownerUid", "==", currentUserUid));
    const unsubContacts = onSnapshot(qContacts, (snap) => {
      setContacts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return () => { unsubPods1(); unsubPods2(); unsubContacts(); };
  }, [currentUserUid, currentUserEmail]);

  const selectedNote = notes.find((n) => n.id === selectedNoteId);
  const podList = Object.values(pods);
  // Owner only if ownerUid matches — never grant ownership to notes missing ownerUid
  const isNoteOwner =
    selectedNote?.ownerUid === currentUserUid ||
    (!selectedNote?.ownerUid && selectedNote?.ownerEmail === currentUserEmail);
  // Any pod member can edit content (title + description)
  const canEditNote =
    isNoteOwner || (selectedNote?.sharedPods || []).some((id) => id in pods);

  const togglePod = (podId, setList) => {
    setList((prev) =>
      prev.includes(podId) ? prev.filter((id) => id !== podId) : [...prev, podId]
    );
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    // memberEmails drives server-side read access. Always include the owner plus
    // every current member of each shared pod.
    const memberEmails = Array.from(new Set([
      currentUserEmail,
      ...selectedPodIds.flatMap((podId) => pods[podId]?.members || []),
    ]));
    const now = Date.now();
    const noteRef = await addDoc(collection(db, "notes"), {
      title: title.trim(),
      description: description.trim(),
      sharedPods: selectedPodIds,
      memberEmails,
      ownerUid: currentUserUid,
      ownerEmail: currentUserEmail,
      createdAt: now,
      updatedAt: now,
      updatedBy: currentUserEmail,
    });
    // Notify each pod member (deduplicated) that a new note was shared with them.
    if (selectedPodIds.length > 0) {
      const recipients = new Map();
      for (const podId of selectedPodIds) {
        const pod = pods[podId];
        if (!pod) continue;
        for (const email of pod.members || []) {
          if (email !== currentUserEmail && !recipients.has(email)) {
            recipients.set(email, pod.name);
          }
        }
      }
      await Promise.all(
        Array.from(recipients.entries()).map(([email, podName]) =>
          addDoc(collection(db, "notifications"), {
            type: "note_shared",
            actorEmail: currentUserEmail,
            recipientEmail: email,
            noteTitle: title.trim(),
            noteId: noteRef.id,
            podName,
            status: "unread",
            createdAt: now,
          })
        )
      );
    }
    setTitle("");
    setDescription("");
    setSelectedPodIds([]);
    setShowAddNoteModal(false);
  };

  // Save a note's title/body. Each save is tied to the note that was being edited, so a pending
  // autosave can never land on a different note after switching.
  const saveNoteContent = async (noteId, newTitle, newDesc) => {
    const note = allNotesRef.current.find((n) => n.id === noteId);
    if (!note) return;
    const t = newTitle.trim();
    const d = newDesc.trim();
    if (t === (note.title || "") && d === (note.description || "")) return;
    try {
      await updateDoc(doc(db, "notes", noteId), {
        title: t,
        description: d,
        updatedAt: Date.now(),
        updatedBy: currentUserEmail, // who made the last edit (shown in the Dashboard's What's new)
      });
    } catch (err) {
      alert("Could not save the note: " + err.message);
    }
  };

  // Autosave: 2s after typing stops; blur saves immediately; leaving the page flushes anything pending.
  const scheduleAutosave = (newTitle, newDesc) => {
    if (!selectedNote || !canEditNote) return;
    clearTimeout(autosaveRef.current?.timer);
    const pending = { noteId: selectedNote.id, title: newTitle, desc: newDesc };
    pending.timer = setTimeout(async () => {
      autosaveRef.current = null;
      await saveNoteContent(pending.noteId, pending.title, pending.desc);
      if (!autosaveRef.current) setSaveState("saved");
    }, 2000);
    autosaveRef.current = pending;
    setSaveState("saving");
  };
  const saveInline = (newTitle, newDesc) => {
    if (!selectedNote) return;
    clearTimeout(autosaveRef.current?.timer);
    const wasPending = autosaveRef.current;
    autosaveRef.current = null;
    saveNoteContent(selectedNote.id, newTitle, newDesc).then(() => { if (wasPending && !autosaveRef.current) setSaveState("saved"); });
  };

  const toggleSharedPod = async (podId) => {
    if (!selectedNote || !isNoteOwner) return;
    const isAdding = !editSharedPods.includes(podId);
    const next = isAdding
      ? [...editSharedPods, podId]
      : editSharedPods.filter((id) => id !== podId);
    setEditSharedPods(next);
    const now = Date.now();
    const ref = doc(db, "notes", selectedNote.id);
    if (isAdding) {
      const add = pods[podId]?.members || [];
      await updateDoc(ref, { sharedPods: arrayUnion(podId), ...(add.length && { memberEmails: arrayUnion(...add) }), updatedAt: now, updatedBy: currentUserEmail });
    } else {
      // Drop members no remaining pod covers (never the owner)
      const keep = new Set([currentUserEmail, ...next.flatMap((id) => pods[id]?.members || [])]);
      const drop = (pods[podId]?.members || []).filter((e) => !keep.has(e));
      await updateDoc(ref, { sharedPods: arrayRemove(podId), ...(drop.length && { memberEmails: arrayRemove(...drop) }), updatedAt: now, updatedBy: currentUserEmail });
    }
    // Notify members of the newly added pod.
    if (isAdding) {
      const pod = pods[podId];
      if (pod) {
        await Promise.all(
          (pod.members || [])
            .filter((email) => email !== currentUserEmail)
            .map((email) =>
              addDoc(collection(db, "notifications"), {
                type: "note_shared",
                actorEmail: currentUserEmail,
                recipientEmail: email,
                noteTitle: selectedNote.title || "Untitled note",
                noteId: selectedNote.id,
                podName: pod.name,
                status: "unread",
                createdAt: now,
              })
            )
        );
      }
    }
  };

  const deleteNote = async () => {
    if (!selectedNote) return;
    const ref = doc(db, "notes", selectedNote.id);
    if (isNoteOwner) {
      if (!window.confirm("Delete this note for everyone in the pod?")) return;
      deletingRef.current = true;
      setSelectedNoteId(null);
      setMobileDetailOpen(false);
      await deleteDoc(ref);
    } else {
      if (!window.confirm("Remove this note from your view? It will stay on the pod.")) return;
      deletingRef.current = true;
      setSelectedNoteId(null);
      setMobileDetailOpen(false);
      await updateDoc(ref, { hiddenBy: arrayUnion(currentUserEmail) });
    }
  };

  const nameFor = (email) => {
    if (!email) return "";
    if (email === currentUserEmail) return "You";
    const c = contacts.find((ct) => ct.email === email);
    return (c && (`${c.firstName || ""} ${c.lastName || ""}`.trim() || c.name)) || email.split("@")[0];
  };
  const isMine = (n) => n.ownerUid === currentUserUid || (!n.ownerUid && n.ownerEmail === currentUserEmail);
  const q = search.trim().toLowerCase();
  const shown = notes.filter((n) =>
    (filter === "all" || (filter === "mine" ? isMine(n) : (n.sharedPods || []).includes(filter))) &&
    (!q || `${n.title || ""}\n${n.description || ""}`.toLowerCase().includes(q)));
  const openNote = (id) => { setSelectedNoteId(id); setMobileDetailOpen(true); document.querySelector(".app-main")?.scrollTo(0, 0); };
  const closeMenus = () => setMenu(null);

  // Phones: swipe right on an open note to go back to the list. The note follows the finger once the
  // swipe is clearly sideways; past 80px (or a quick flick) it slides away. Vertical scrolling is untouched.
  const detailShown = isMobile && mobileDetailOpen && !!selectedNote;
  useEffect(() => {
    const el = editorRef.current;
    if (!detailShown || !el) return;
    let x0 = 0, y0 = 0, t0 = 0, dx = 0, mode = null; // mode: null (undecided) | "swipe" | "scroll"
    const start = (e) => {
      if (e.touches.length !== 1) { mode = "scroll"; return; }
      x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; t0 = Date.now(); dx = 0; mode = null;
    };
    const move = (e) => {
      if (mode === "scroll") return;
      const mx = e.touches[0].clientX - x0, my = e.touches[0].clientY - y0;
      if (mode === null) {
        if (Math.abs(mx) < 10 && Math.abs(my) < 10) return;
        mode = mx > 0 && Math.abs(mx) > Math.abs(my) * 1.5 ? "swipe" : "scroll";
        if (mode === "scroll") return;
        el.style.transition = "none";
      }
      e.preventDefault(); // keep the page from scrolling while the note slides
      dx = Math.max(0, mx);
      el.style.transform = `translateX(${dx}px)`;
    };
    const end = () => {
      if (mode !== "swipe") return;
      mode = null;
      const back = dx > 80 || (dx > 30 && dx / Math.max(1, Date.now() - t0) > 0.5);
      el.style.transition = "transform .2s ease";
      el.style.transform = back ? "translateX(100%)" : "";
      if (back) setTimeout(() => { el.style.transition = ""; el.style.transform = ""; setMobileDetailOpen(false); }, 180);
    };
    el.addEventListener("touchstart", start, { passive: true });
    el.addEventListener("touchmove", move, { passive: false });
    el.addEventListener("touchend", end, { passive: true });
    el.addEventListener("touchcancel", end, { passive: true });
    return () => {
      el.removeEventListener("touchstart", start);
      el.removeEventListener("touchmove", move);
      el.removeEventListener("touchend", end);
      el.removeEventListener("touchcancel", end);
    };
  }, [detailShown, selectedNoteId]);

  // Menus close on outside click or Esc
  useEffect(() => {
    if (!menu) return;
    const onDown = (e) => { if (!e.target.closest?.(".note-menuwrap")) setMenu(null); };
    const onKey = (e) => { if (e.key === "Escape") setMenu(null); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [menu]);

  const editedLine = (n) => {
    const ts = n.updatedAt || n.createdAt;
    const who = n.updatedBy ? nameFor(n.updatedBy) : null;
    const when = ts ? timeAgo(ts) : "";
    return who ? `${who} edited ${when.replace(/^Just now$/, "just now")}` : when;
  };

  let group = "";
  return (
    <div className={`pg${mobileDetailOpen && selectedNote ? " notes--open" : ""}`}>
      <div className="pg-head">
        <h1 className="section-title pg-h1">Notes</h1>
        <div className="pg-head__actions">
          <label className="pg-search">{SEARCH_ICON}<input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search notes" aria-label="Search notes" /></label>
          <button type="button" className="btn btn--primary pg-btn" onClick={() => setShowAddNoteModal(true)}><PlusIcon />New note</button>
        </div>
      </div>

      {podList.length > 0 && (
        <div className="note-filters" role="group" aria-label="Show">
          {[["all", "All"], ["mine", "Mine"]].map(([k, l]) => (
            <button key={k} type="button" className="note-filter" aria-pressed={filter === k} onClick={() => setFilter(k)}>{l}</button>
          ))}
          {podList.map((p) => (
            <button key={p.id} type="button" className="note-filter" aria-pressed={filter === p.id} onClick={() => setFilter(filter === p.id ? "all" : p.id)}>
              <i style={{ background: getPodColor(p.id).border }} />{p.name}
            </button>
          ))}
        </div>
      )}

      <div className={`pg-two${mobileDetailOpen && selectedNote ? " pg-two--open" : ""}`}>
        <section className="card pg-list" aria-label="Notes">
          {notes.length === 0 && <p className="dash-muted pg-pad">No notes yet. Start one for a packing list, gift ideas or plans with a pod.</p>}
          {notes.length > 0 && shown.length === 0 && <p className="dash-muted pg-pad">No notes match.</p>}
          {shown.map((note) => {
            const g = groupOf(note.updatedAt || note.createdAt);
            const head = g !== group ? (group = g) : null;
            const notePods = (note.sharedPods || []).map((id) => pods[id]).filter(Boolean);
            const firstLine = (note.description || "").split("\n").find((l) => l.trim()) || "";
            return (
              <div key={note.id} className="pg-group">
                {head && <div className="pg-letter">{head}</div>}
                <button type="button" className="note-row" aria-current={note.id === selectedNoteId} onClick={() => openNote(note.id)}>
                  <span className="note-row__top"><b>{note.title || "Untitled note"}</b><span>{timeAgo(note.updatedAt || note.createdAt)}</span></span>
                  {firstLine && <span className="note-row__pv">{firstLine}</span>}
                  <span className="note-row__meta">
                    {notePods.length ? <><i className="pg-dot" style={{ background: getPodColor(notePods[0].id).border }} />{notePods[0].name}{notePods.length > 1 ? ` +${notePods.length - 1}` : ""}</> : "Only you"}
                    {note.updatedBy ? ` · ${nameFor(note.updatedBy)} edited` : ""}
                  </span>
                </button>
              </div>
            );
          })}
        </section>

        {selectedNote ? (
          <section ref={editorRef} className="card pg-detail note-ed" aria-label="Note">
            <div className="note-ed__bar">
              <button type="button" className="pg-back" onClick={() => setMobileDetailOpen(false)}><BackIcon />Notes</button>
              <div className="note-ed__share">
                {editSharedPods.length === 0 && <span className="note-ed__only">Only you</span>}
                {editSharedPods.map((podId) => {
                  const pod = pods[podId];
                  if (!pod) return null;
                  const c = getPodColor(podId);
                  return (
                    <span key={podId} className="pg-podchip note-podchip" style={{ background: c.bg, color: c.text }}>
                      {pod.name}
                      {isNoteOwner && <button type="button" aria-label={`Stop sharing with ${pod.name}`} onClick={() => toggleSharedPod(podId)}>×</button>}
                    </span>
                  );
                })}
                {isNoteOwner && podList.some((p) => !editSharedPods.includes(p.id)) && (
                  <span className="note-menuwrap pg-menuwrap">
                    <button type="button" className="note-addpod" aria-expanded={menu === "share"} onClick={() => setMenu(menu === "share" ? null : "share")}>+ Share with a pod</button>
                    {menu === "share" && (
                      <span className="pg-menu" role="menu">
                        {podList.filter((p) => !editSharedPods.includes(p.id)).map((p) => (
                          <button key={p.id} type="button" role="menuitem" onClick={() => { closeMenus(); toggleSharedPod(p.id); }}>
                            <i style={{ background: getPodColor(p.id).border }} />{p.name}
                          </button>
                        ))}
                      </span>
                    )}
                  </span>
                )}
              </div>
              <div className="note-ed__end">
                <span className="note-status" aria-live="polite">
                  {!canEditNote ? "View only" : saveState === "saving" ? "Saving…" : <>{CHECK_ICON}Saved</>}
                </span>
                <span className="note-menuwrap pg-menuwrap">
                  <button type="button" className="pg-iconbtn" aria-label="More" aria-expanded={menu === "more"} onClick={() => setMenu(menu === "more" ? null : "more")}>{MENU_ICON}</button>
                  {menu === "more" && (
                    <span className="pg-menu pg-menu--end" role="menu">
                      <button type="button" role="menuitem" className="pg-menu__danger" onClick={() => { closeMenus(); deleteNote(); }}>
                        <TrashIcon />{isNoteOwner ? "Delete note" : "Remove from my notes"}
                      </button>
                    </span>
                  )}
                </span>
              </div>
            </div>
            <input
              className="note-ed__title"
              value={editTitle}
              onChange={(e) => { setEditTitle(e.target.value); scheduleAutosave(e.target.value, editDescription); }}
              onBlur={() => saveInline(editTitle, editDescription)}
              readOnly={!canEditNote}
              placeholder="Untitled note"
              aria-label="Title"
            />
            <div className="note-ed__meta">
              {isNoteOwner ? "Yours" : `Shared by ${nameFor(selectedNote.ownerEmail)}`}
              {(selectedNote.updatedAt || selectedNote.createdAt) ? ` · ${editedLine(selectedNote)}` : ""}
            </div>
            <textarea
              className="note-ed__body"
              value={editDescription}
              onChange={(e) => { setEditDescription(e.target.value); scheduleAutosave(editTitle, e.target.value); }}
              onBlur={() => saveInline(editTitle, editDescription)}
              readOnly={!canEditNote}
              placeholder="Write something…"
              aria-label="Note"
            />
          </section>
        ) : (
          notes.length > 0 && <section className="card pg-detail pg-detail--empty"><p className="dash-muted">Pick a note to read it.</p></section>
        )}
      </div>

      {showAddNoteModal && createPortal(
        <div className="modal-overlay" onClick={() => { setShowAddNoteModal(false); setSelectedPodIds([]); }} onTouchMove={(e) => e.stopPropagation()}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header">
              <div className="modal__title">New note</div>
              <button type="button" className="btn btn--ghost btn--sm" aria-label="Close" onClick={() => { setShowAddNoteModal(false); setSelectedPodIds([]); }}>✕</button>
            </div>
            <div className="modal__content">
              <form onSubmit={addNote}>
                <div className="form-group">
                  <label className="form-label" htmlFor="note-new-title">Title</label>
                  <input id="note-new-title" className="form-input" placeholder="e.g. Packing list" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus={!isMobile} />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="note-new-body">Note</label>
                  <textarea id="note-new-body" className="form-textarea" placeholder="Write something…" value={description} onChange={(e) => setDescription(e.target.value)} rows={6} />
                </div>
                <div className="form-group">
                  <div className="form-label">Share with</div>
                  {podList.length === 0 ? (
                    <div className="ef-hint">Only you. Create a pod in the Pods tab to share notes.</div>
                  ) : (
                    <div className="ef-pods">
                      {podList.map((p) => {
                        const c = getPodColor(p.id);
                        const on = selectedPodIds.includes(p.id);
                        return (
                          <button key={p.id} type="button" className="ef-pod" aria-pressed={on} onClick={() => togglePod(p.id, setSelectedPodIds)}
                            style={on ? { background: c.bg, color: c.text, borderColor: c.border } : undefined}>
                            <i style={{ background: c.border }} />{p.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                  <button type="submit" className="btn btn--primary pg-btn" disabled={!title.trim()}>Add note</button>
                </div>
              </form>
            </div>
          </div>
        </div>,
      document.querySelector('.app-shell') || document.body
      )}
    </div>
  );
}
